import React, { FC, useEffect, useMemo, useState } from 'react'
import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid'
import { useStyles } from './styles'
import { Box, debounce, Tab, Tabs, Typography, CircularProgress } from '@material-ui/core'
import { TabContext, TabPanel } from '@material-ui/lab'
import { CustomDataGrid } from '../../common'
import { getCompanies, getWatchlistCompanies, toggleCompanyHiddenStatus, saveUserPortfolioDetails } from '../../../services/bff-service'
import { useDispatch, useSelector } from 'react-redux'
import { setSnackBar } from '../../../stores/ui/ui-action'
import { BookClosureDashboard } from '../book-closure-dashboard'
import CONSTANTS from '../../../utils/constants'
import { isUserAdmin } from '../../../utils/identity-utils'
import { getColumnGroupingModel, getColumnsConfig } from './config'
import { EmptyCompanyView, NoTeamCodeFilterView } from '../../common/empty-status-views'
import { setUserPortfolioCompanies, setUserPortfolioId } from '../../../stores/user/user-action'
import * as filters from './list-filters'
import {CompaniesSearchBy} from '../companies-search-by'
import {ControlledSwitch} from '../../common/controlled-switch'
import ListTabs from './company-list-all-filter'
import {QuickChipFilter} from '../companies-search-by/quick-chip-filter'
import {AddCompanyButton} from './add-company-button'
import {PortfolioSetupInfo} from './portfolio-setup-info'
import {BookClosureTabLabel} from './bookclosure-tab-label'
import { TeamCodeFilter } from "./team-code-filter";
import { setAllCompanyList } from '../../../stores/company/company-action'
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLinkWithSrc } from '../../../shared/hooks/use-route-navigator'

const {
  FDM_DASHBOARD: {
    TAB_CONFIGS: { COMPANIES, ALL_COMPANIES, PORTFOLIO },
  },
} = CONSTANTS;

export const CompanyList: FC<any> = () => {
    const dispatch = useDispatch()
    const styles = useStyles()
    const user = useSelector((state: any) => state.user)

    // User stuff
    const isAdmin = isUserAdmin(user)
    const { userTeamCodes = [], userCompanies = [], portfolioId = '' } = user || {}

    // Ie. Companies or Book Closure
    const navigate = useNavigate();
    const linkWithSrcInstance = useLinkWithSrc();
    
    const { layout }: any = useParams();
    const { search } = useLocation();
    const activeTab = new URLSearchParams(search).get('tab') ?? PORTFOLIO.value;

    // Companies list and filter stuff
    const [areCompaniesLoading, setAreCompaniesLoading] = useState(true);
    const [xCompanies, setXCompanies] = useState([])
    const [watchlistCompanies, setWatchlistCompanies] = useState([])
    const [pageSize, setPageSize] = useState(50)

    const { isAllCompanies, isMyPortfolio } = filters.getActiveTab(activeTab)

    const [searchText, setSearchText] = useState('')
    const [isHiddenStatusFilterOn, toggleHiddenStatusFilter] = useState<boolean>(false)
    const [hiddenStatusChange, setHiddenStatusChange] = useState(null)
    const [isActiveStatusFilterOn, toggleInActiveStatusFilter] = useState<boolean>(false)
    const [isApplyTeamCodeFilterOn, toggleApplyTeamCodeFilter] = useState<boolean>(false)
    const [selectedChips, setSelectChips] = useState([])


    // The final filtered companies for displaying
    const companiesForCurrentTab = filters.byCurrentTab(activeTab, xCompanies, watchlistCompanies, userCompanies)
    const displayedCompanies  = filters.bySearchAndSelections(
        companiesForCurrentTab,
        searchText,
        isActiveStatusFilterOn,
        isApplyTeamCodeFilterOn,
        isHiddenStatusFilterOn,
        isMyPortfolio,
        userTeamCodes,
        selectedChips)

    const issueCounts = filters.countIssues(displayedCompanies)

    const handleToggleTeamCodeFilter = (value: boolean, handleTeamCodeToggle) => {
        handleTeamCodeToggle(value)
    }

    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
       
        switch(newValue){
            case 'book-closure-dashboard':
                navigate(`/svber/data-management/companies/book-closure-dashboard`);
                return ;
            default:
                    toggleApplyTeamCodeFilter(false)
                    toggleInActiveStatusFilter(false)
                    toggleHiddenStatusFilter(false)
                    setSearchText('')
                    navigate(`/svber/data-management/companies/view?tab=portfolio`);
                return;
        }

    }

    const toggleHiddenStatusByCompanyID = (actionType: string, rowData: any) => {
        const isHidden = actionType === 'HIDE' ? true : false
        const { userEmail, userEntityMappingId, companyId, dbPlatform } = rowData
        setAreCompaniesLoading(true)
        const payload = {
            userEmail,
            userEntityMappingId,
            companyId,
            dbPlatform,
            hidden: isHidden,
        }
        const message = isHidden ? 'Company hidden.' : 'Company unhidden.'
        toggleCompanyHiddenStatus(payload)
            .then(() => setHiddenStatusChange(!hiddenStatusChange))
            .then(() => {
                dispatch(
                    setSnackBar({
                        show: true,
                        message,
                        messageType: 'success',
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                        },
                    })
                )
            })
            .catch((error) => {
                dispatch(
                    setSnackBar({
                        show: true,
                        message: 'Oops! Something went wrong',
                        description: error.message,
                        messageType: 'error',
                    })
                )
            })
            .finally(() => {
                setAreCompaniesLoading(false)
            })
    }

    const modifyMyPortfolioCompanyList = async (value, actionType) => {
        try {
            const updatedCompanyIdList = actionType === 'ADD' ? [...userCompanies, value] : userCompanies.filter(company => company !== value)

            const response = await saveUserPortfolioDetails({
                portfolio_id: portfolioId,
                portfolio: {
                    teamCodes: userTeamCodes,
                    companies: updatedCompanyIdList,
                },
            })

            // If response has a portfolio_id, then this portfolio is new. Update the user with the new portfolio ID.
            // Note: the following dispatch also updates the user, so might be best to update both pieces of information at once.
            if (response?.data?.portfolio_id)
                dispatch(setUserPortfolioId(response.data.portfolio_id))

            dispatch(setUserPortfolioCompanies(updatedCompanyIdList))
            dispatch(
                setSnackBar({
                    show: true,
                    message: actionType === 'ADD' ? 'Company added to My Portfolio' : 'Company removed from My Portfolio',
                    messageType: 'success',
                })
            )
        } catch (error) {
            dispatch(
                setSnackBar({
                    show: true,
                    message: actionType === 'ADD' ? 'Unable to add company to My Portfolio' : 'Unable to remove company from My Portfolio' ,
                    messageType: 'error',
                })
            )
        }
    }

    const columnGroupingModel: GridColumnGroupingModel = getColumnGroupingModel(isAdmin, styles)

    const columns: GridColDef[] = getColumnsConfig(
        styles,
        isAdmin,
        isMyPortfolio,
        activeTab,
        toggleHiddenStatusByCompanyID,
        modifyMyPortfolioCompanyList,
        linkWithSrcInstance
    )

    const filterHandlers = useMemo(() => {
        const handlers = {
            handleCompanySearch: debounce(setSearchText, 300),
            setFilterType: activeTab,
            handleTeamCodeToggle: toggleApplyTeamCodeFilter,
            handleInActiveCompanyToggle: toggleInActiveStatusFilter,
            handleHiddenCompanyToggle: toggleHiddenStatusFilter,
            setSelectChips: setSelectChips,

        }
        return handlers
    }, [])

    const renderCompanyGrid = () => {
        if (isMyPortfolio && !userCompanies.length && (!isApplyTeamCodeFilterOn || !userTeamCodes.length)) {
            return !userTeamCodes.length ?  <EmptyCompanyView /> : <NoTeamCodeFilterView />
        }

        return (
            <CustomDataGrid
                columns={columns}
                rows={displayedCompanies ?? []}
                loading={areCompaniesLoading}
                columnGroupingModel={columnGroupingModel}
                experimentalFeatures={{ columnGrouping: true }}
                getRowId={(row: any) => row?.companyId ?? row?.userEmail}
                pageSize={pageSize}
                onPageSizeChange={() => setPageSize(pageSize)}
                className={styles.tableWrapper}
            />
        )
    }

    // Gets all data from external sources. Should run on the initial render and when clicking between My Portfolio,
    // Watchlist, and Companies (and run no other time).
    useEffect(() => {
        const abortControllerInstance = new AbortController();
        setAreCompaniesLoading(true)
        Promise.all([getCompanies({ signal: abortControllerInstance.signal }), getWatchlistCompanies({ signal: abortControllerInstance.signal })])
            .then(([xCompaniesDb, watchListCompaniesDb]) => {
                dispatch(setAllCompanyList(xCompaniesDb.results));
                setXCompanies(xCompaniesDb.results)
                setWatchlistCompanies(watchListCompaniesDb.results)
            })
            .catch(e => {
                if(e.message === 'canceled') return;
                dispatch(
                    setSnackBar({
                        show: true,
                        message: 'Error during setup, please refresh and try again momentarily.',
                        description: e.message,
                        messageType: 'error'
                    }))
            })
            .finally(() => setAreCompaniesLoading(false))

        // This could alternatively go in the company-list-all-filter, but then would require prop-drilling these
        // functions.
        if (activeTab === ALL_COMPANIES.value) {
            toggleInActiveStatusFilter(false)
            setSelectChips([])
        }
        return () => {
            abortControllerInstance.abort();
        }
    }, [activeTab, hiddenStatusChange])

    return (
        <>
            <Box className={styles.container}>
                <div className={styles.paper}>
                    <TabContext value={layout}>
                        {/* Companies and Book Closures Tabs */}
                        <Tabs
                            className={styles.rootClass}
                            value={layout}
                            onChange={handleTabChange}
                            variant='standard'
                            textColor='primary'
                            indicatorColor='primary'
                        >
                            <Tab label={COMPANIES.label} className={styles.tabLabelTypography} value={'view'} data-testid='companies' />
                            <Tab label={<BookClosureTabLabel />} value={'book-closure-dashboard'} data-testid='book-closure-dashboard' />
                        </Tabs>

                        {/* Companies Section */}
                        <TabPanel value='view' className={`${styles.fullWidth} ${styles.p0} ${styles.tabWrapper}`}>

                            {/* Filters */}
                            <div className={styles.filtersWrapper}>

                                {/*Search Text*/}
                                <Box display={'flex'} gridGap={'20px'} order={1}>
                                    <CompaniesSearchBy handleSearch={filterHandlers.handleCompanySearch} defaultSearchText={searchText} />
                                </Box>

                                {/*Toggles/Switches*/}
                                <Box order={3} display={'flex'} gridGap={'20px'}>
                                    <ControlledSwitch
                                        label={'Show My Team Codes'}
                                        defaultStatus={isApplyTeamCodeFilterOn}
                                        handleSwitchToggle={(value) => handleToggleTeamCodeFilter(value, filterHandlers.handleTeamCodeToggle)}
                                        dataTestId='apply-team-code-toggle'
                                        infoTooltip={'Enable this setting so that your team codes display across My Portfolio, Watchlist, and All Companies.'}
                                        switchTooltip={'No team codes selected yet.'}
                                    />
                                    <ControlledSwitch
                                        label={CONSTANTS.FDM_DASHBOARD.SHOW_INACTIVE_COMPANIES}
                                        defaultStatus={isActiveStatusFilterOn}
                                        handleSwitchToggle={filterHandlers.handleInActiveCompanyToggle}
                                        dataTestId='show-inactive-companies-switch'
                                    />
                                    {isAdmin && (
                                        <ControlledSwitch
                                            label={CONSTANTS.FDM_DASHBOARD.DISPLAY_HIDDEN_COMPANIES}
                                            defaultStatus={isHiddenStatusFilterOn}
                                            handleSwitchToggle={filterHandlers.handleHiddenCompanyToggle}
                                            dataTestId='display-hidden-companies-switch'
                                        />
                                    )}
                                </Box>

                                {/* Add Company & Team Code Filter (they occupy the same position) */}
                                {!isMyPortfolio && isAdmin && (
                                    <Box order={4} ml={'auto'}>
                                        <AddCompanyButton />
                                    </Box>
                                )}
                                <TeamCodeFilter
                                    handleTeamCodeToggle={filterHandlers.handleTeamCodeToggle} 
                                    inputStyle={styles.teamCodeSelect}
                                />

                            </div>

                            {/* List Tabs (My Portfolio, Watchlist, Companies */}
                            {/* And PortfolioSetupInfo */}
                            <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                                <ListTabs />
                                <PortfolioSetupInfo />
                            </Box>

                            {/* Quick Filter Chip s*/}
                            <Box display={isAllCompanies && 'none'}>
                                <QuickChipFilter
                                    selectedChips={selectedChips}
                                    setSelectChips={setSelectChips}
                                    issueCounts={issueCounts}
                                ></QuickChipFilter>
                            </Box>

                            {/* And finally, the actual Companies List grid... */}
                            {
                                areCompaniesLoading ?
                                    <Box marginLeft={'auto'} marginRight={'auto'}><CircularProgress size={24} /></Box>
                                    : renderCompanyGrid()
                            }
                        </TabPanel>

                        {/* Book Closures Section */}
                        <TabPanel value='book-closure-dashboard' className={`${styles.fullWidth} ${styles.p0}`}>
                            <BookClosureDashboard />
                        </TabPanel>
                    </TabContext>
                </div>
            </Box>
        </>
    )
}
